import { useState, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search, RotateCcw, AlertCircle, TrendingUp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { DEFAULT_PAGE_SIZE, ListPagination } from '@/components/common/ListPagination'
import { DateRangePicker } from '@/components/DateRangePicker'
import { performanceApi } from '@/api/performance'
import type { PerformanceListParams } from '@/types/performance'

export function PerformanceListPage() {
  const [params, setParams] = useState<PerformanceListParams>({
    page: 1,
    page_size: DEFAULT_PAGE_SIZE,
  })

  const { data, isLoading, error } = useQuery({
    queryKey: ['performances', 'list', params],
    queryFn: () => performanceApi.getList(params),
    placeholderData: (prev) => prev,
  })

  const handleSearch = () => setParams((prev) => ({ ...prev, page: 1 }))
  const handleReset = () =>
    setParams({ page: 1, page_size: DEFAULT_PAGE_SIZE })

  const handlePageChange = useCallback((page: number) => {
    setParams((prev) => ({ ...prev, page }))
  }, [])

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setParams((prev) => ({ ...prev, page: 1, page_size: pageSize }))
  }, [])

  const list = data?.list || []

  return (
    <div className="space-y-5">
      {/* 页面标题 */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">业绩明细</h1>
        <p className="mt-1 text-sm text-muted-foreground">手动订单创建时按扣款制计算的员工业绩记录</p>
      </div>

      {/* 错误提示 */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error instanceof Error ? error.message : '加载数据失败，请稍后重试'}
          </AlertDescription>
        </Alert>
      )}

      {/* 筛选栏 */}
      <Card className="rounded-lg">
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative w-full max-w-xs">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="搜索订单号..."
                value={params.order_no || ''}
                onChange={(e) =>
                  setParams((prev) => ({ ...prev, order_no: e.target.value || undefined }))
                }
                className="pl-9"
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>

            <DateRangePicker
              value={
                params.start_date && params.end_date
                  ? { start_date: params.start_date, end_date: params.end_date }
                  : undefined
              }
              onChange={(v) =>
                setParams((prev) => ({
                  ...prev,
                  start_date: v.start_date,
                  end_date: v.end_date,
                }))
              }
            />

            <Button type="button" onClick={handleSearch} disabled={isLoading}>
              <Search className="mr-1.5 h-4 w-4" /> 查询
            </Button>
            <Button type="button" variant="outline" onClick={handleReset}>
              <RotateCcw className="mr-1.5 h-4 w-4" /> 重置
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* 表格 */}
      {isLoading && !data ? (
        <div className="rounded-xl border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                {Array.from({ length: 8 }).map((_, i) => (
                  <TableHead key={i}><Skeleton className="h-4 w-20" /></TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 8 }).map((_, j) => (
                    <TableCell key={j}><Skeleton className="h-4 w-20" /></TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : list.length === 0 ? (
        <div className="rounded-xl border bg-card py-12 text-center">
          <TrendingUp className="mx-auto mb-3 h-10 w-10 text-muted-foreground/50" />
          <p className="text-muted-foreground">暂无业绩记录</p>
        </div>
      ) : (
        <div className="rounded-xl border bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <Table className="min-w-[1200px]">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>订单号</TableHead>
                  <TableHead>客户手机</TableHead>
                  <TableHead className="text-right">订单金额</TableHead>
                  <TableHead className="text-right">推广扣款</TableHead>
                  <TableHead className="text-right">本金扣款</TableHead>
                  <TableHead className="text-right">促销扣款</TableHead>
                  <TableHead className="text-right">仓管扣款</TableHead>
                  <TableHead className="text-right">净业绩</TableHead>
                  <TableHead className="text-right">创建人业绩</TableHead>
                  <TableHead className="text-right">跟踪人业绩</TableHead>
                  <TableHead>新开</TableHead>
                  <TableHead className="text-right">退款金额</TableHead>
                  <TableHead>创建时间</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.map((p) => (
                  <TableRow key={p.id} className="group">
                    <TableCell>
                      <span className="font-mono text-sm">{p.order_no}</span>
                    </TableCell>
                    <TableCell className="text-sm">{p.customer_phone || '-'}</TableCell>
                    <TableCell className="text-right text-sm">¥{(p.pay_price ?? 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right text-sm text-muted-foreground">¥{(p.cut_promotion ?? 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right text-sm text-muted-foreground">¥{(p.cut_cost ?? 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right text-sm text-muted-foreground">¥{(p.cut_marketing ?? 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right text-sm text-muted-foreground">¥{(p.cut_warehouse ?? 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right">
                      <span className="font-semibold">¥{(p.net_performance ?? 0).toFixed(2)}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="text-sm font-medium text-primary">¥{(p.creator_amount ?? 0).toFixed(2)}</span>
                    </TableCell>
                    <TableCell className="text-right text-sm">
                      {p.tracker_id ? `¥${(p.tracker_amount ?? 0).toFixed(2)}` : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={p.is_new ? 'success' : 'secondary'}>
                        {p.is_new ? '新开' : '复购'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right text-sm">
                      {p.refund_amount > 0 ? (
                        <span className="text-destructive">¥{p.refund_amount.toFixed(2)}</span>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{p.created_at}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* 分页 */}
      <ListPagination
        page={params.page}
        pageSize={params.page_size}
        total={data?.total || 0}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
    </div>
  )
}
